import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Download, MoveRight, Calendar, Globe, MapPin, Star, Layers } from 'lucide-react'
import { Button } from '@workspace/ui/components/Button'
import { useTranslation } from 'react-i18next'
import { BsDateRangePicker, BsDateRangePickerValue } from '@workspace/ui/components/DatePicker'
import { DataTable } from '@workspace/ui/components/DataTable'
import { Badge } from '@workspace/ui/components/Badge'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { ColumnDef } from '@tanstack/react-table'
import { useDateRangeQuickOptions } from '../shared/hooks/useDateRangeQuickOptions'
import { useDashboardSummary } from '../shared/hooks/dashboard/useDashboardSummary'
import { api } from '../shared/lib/api'
import { ProjectDetail, TaskWithFolderPaths } from '@workspace/lib/api/project'
import { useMemo, useState, useEffect } from 'react'
import { useUser } from '@/shared/store/auth.store'
import { useRegions } from '@/shared/hooks/useRegions'
import { useAreas } from '@/shared/hooks/useAreas'
import { BsSelect } from '@workspace/ui/components/Select'
import dayjs from 'dayjs'
import { TaskDetailDialog } from '@/shared/components/legal-documents/TaskDetailDialog'
import { auditLogHelpers } from '@/shared/lib/audit-log'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { cn } from '@workspace/ui/lib/utils'

export const Route = createFileRoute('/dashboard')({
    component: AdminDashboard,
})

function AdminDashboard() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const quickOptions = useDateRangeQuickOptions()

    // Track dashboard view
    useEffect(() => {
        auditLogHelpers.trackView('system', 'dashboard', { description: t('audit.view.dashboard') }).catch(() => {})
    }, [t])

    const getPriorityColorForBadge = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'var(--color-red-500)'
            case 'medium':
                return 'var(--primary)'
            case 'low':
                return 'var(--color-green-500)'
            default:
                return 'var(--color-gray-500)'
        }
    }

    const getPriorityLabel = (priority: string) => {
        switch (priority) {
            case 'high':
                return t('dashboard.priority.high')
            case 'medium':
                return t('dashboard.priority.medium')
            case 'low':
                return t('dashboard.priority.low')
            default:
                return priority
        }
    }

    // State for date range picker
    const [dateRange, setDateRange] = useState<BsDateRangePickerValue>({
        start: null,
        end: null,
    })
    const [selectedTask, setSelectedTask] = useState<TaskWithFolderPaths | null>(null)
    const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false)
    const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>(undefined)
    const [selectedAreaId, setSelectedAreaId] = useState<string>('ALL')

    const user = useUser()
    const isSuperAdmin = user?.role?.name === ADMIN_ROLE_NAME

    // Fetch regions only for super admin
    const { data: regionsResponse } = useRegions({ enabled: isSuperAdmin, pageSize: 100 })
    const regionOptions = useMemo(() => {
        const regions = regionsResponse?.data || []
        return [
            { id: 'ALL', name: t('dashboard.overview.allRegions'), description: '', created_at: '', updated_at: '' },
            ...regions,
        ]
    }, [regionsResponse, t])

    useEffect(() => {
        if (isSuperAdmin && !selectedRegionId && regionOptions.length > 1) {
            // > 1 because 'ALL' is always there
            const northernRegionOption = regionOptions.find(r => r.name?.toLowerCase().includes('miền bắc'))
            if (northernRegionOption) {
                setSelectedRegionId(northernRegionOption.id)
            } else {
                setSelectedRegionId('ALL')
            }
        }
    }, [isSuperAdmin, selectedRegionId, regionOptions])

    // Reset area selection when region changes
    useEffect(() => {
        setSelectedAreaId('ALL')
    }, [selectedRegionId])

    const activeRegionId = isSuperAdmin ? selectedRegionId : user?.region?.id
    const isAreaQueryEnabled = !isSuperAdmin ? !!activeRegionId : !!selectedRegionId && selectedRegionId !== 'ALL'

    const { data: areasResponse } = useAreas({
        enabled: isAreaQueryEnabled,
        regionIds: activeRegionId && activeRegionId !== 'ALL' ? activeRegionId : undefined,
        pageSize: 100,
    })

    const areaOptions = useMemo(() => {
        const areas = areasResponse?.data || []
        return [{ id: 'ALL', name: t('dashboard.overview.allAreas') }, ...areas.map(a => ({ id: a.id, name: a.name }))]
    }, [areasResponse, t])

    // Format date range for API call
    // When start/end are null => "All time" selected, send no date params so BE returns all data
    const apiParams = useMemo(() => {
        let params: Record<string, any> = {}
        if (dateRange.start && dateRange.end) {
            params.startDate = dayjs(dateRange.start).format('DD/MM/YYYY')
            params.endDate = dayjs(dateRange.end).format('DD/MM/YYYY')
        }
        if (isSuperAdmin && selectedRegionId) {
            params.distributionRegionId = selectedRegionId
        }
        if (selectedAreaId && selectedAreaId !== 'ALL') {
            params.distributionAreaId = selectedAreaId
        }
        return Object.keys(params).length > 0 ? params : undefined
    }, [dateRange, isSuperAdmin, selectedRegionId, selectedAreaId])

    const isDashboardSummaryEnabled = !isSuperAdmin || !!selectedRegionId

    // Fetch dashboard summary data
    const { data: dashboardData, isLoading } = useDashboardSummary(apiParams, isDashboardSummaryEnabled)

    // Export handler
    const handleExport = async () => {
        try {
            const blob = await api.project.exportSummary(apiParams)
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `dashboard-summary-${dayjs().format('YYYY-MM-DD')}.xlsx`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Export failed:', error)
        }
    }

    // Metric cards configuration using real API data
    const metricCards = useMemo(
        () => [
            {
                title: t('dashboard.metrics.totalDocuments.title'),
                value: dashboardData?.totalDocuments?.toString() || '0',
                subtitle: (
                    <span className={`${(dashboardData?.missingDocuments || 0) > 0 ? 'text-red-500' : ''}`}>
                        {t('dashboard.metrics.totalDocuments.subtitle.missing', {
                            count: dashboardData?.missingDocuments || 0,
                        })}
                    </span>
                ),
                icon: <img src="/svg/clipboard-summary.svg" alt="Total Documents" className="w-6 h-6" />,
            },
            {
                title: t('dashboard.metrics.overallProgress.title'),
                value: `${Math.round(dashboardData?.averageProgress || 0)}%`,
                subtitle: '',
                icon: <img src="/svg/sledgehammer.svg" alt="Overall Progress" className="w-6 h-6" />,
            },
            {
                title: t('dashboard.metrics.legalProcesses.title'),
                value: dashboardData?.taskSummary?.totalTasks?.toString() || '0',
                subtitle: (
                    <span className="flex items-center gap-2">
                        <span className="text-background-yellow-dark">
                            {t('dashboard.metrics.legalProcesses.subtitle.inProgress', {
                                count: dashboardData?.taskSummary?.inProgressTasks || 0,
                            })}
                        </span>
                        <span className="text-red-500">
                            {t('dashboard.metrics.legalProcesses.subtitle.overdue', {
                                count: dashboardData?.taskSummary?.overdueTasks || 0,
                            })}
                        </span>
                        <span className="text-blue-500">
                            {t('dashboard.metrics.legalProcesses.subtitle.waitingApproval', {
                                count: dashboardData?.taskSummary?.waitingApprovalTasks || 0,
                            })}
                        </span>
                    </span>
                ),
                icon: <img src="/svg/checklist-summary.svg" alt="Legal Processes" className="w-6 h-6" />,
            },
        ],
        [dashboardData, t],
    )

    // Common background for all metric cards using primary color tokens
    // #FFE79D = primary/200, #FFF9E7 = primary/50
    const cardBgColor = 'radial-gradient(58.15% 100% at 21.32% 0%, var(--primary-200) 0%, var(--primary-50) 100%)'

    const pd = dashboardData?.projectDistribution

    const distCards = useMemo(() => {
        if (!pd) return []

        const fmt = (val: number = 0) => val.toLocaleString('en-US')
        const getSum = (projects: any) => {
            const resCom = (projects?.residentialLandArea || 0) + (projects?.commercialLandArea || 0)
            const other = projects?.otherLandArea || 0
            const totalArea = resCom + other

            const allocResCom =
                (projects?.allocatedResidentialLandArea || 0) + (projects?.allocatedCommercialLandArea || 0)
            const allocOther = projects?.allocatedOtherLandArea || 0
            const totalAllocatedArea = allocResCom + allocOther

            return {
                count: fmt(projects?.count),
                investment: fmt(projects?.totalInvestment),
                totalArea: fmt(totalArea),
                resCom: fmt(resCom),
                other: fmt(other),
                totalAllocatedArea: fmt(totalAllocatedArea),
                allocResCom: fmt(allocResCom),
                allocOther: fmt(allocOther),
            }
        }

        const total = (pd as any).totalProjects || {
            count: 0,
            totalInvestment: 0,
            residentialLandArea: 0,
            commercialLandArea: 0,
            otherLandArea: 0,
            allocatedResidentialLandArea: 0,
            allocatedCommercialLandArea: 0,
            allocatedOtherLandArea: 0,
        }

        return [
            {
                title: t('dashboard.overview.totalAll'),
                icon: <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B]" />,
                data: getSum(total),
            },
            {
                title:
                    pd.regionName === 'Tất cả vùng' || pd.regionName === t('dashboard.overview.allRegions')
                        ? t('dashboard.overview.allRegionsUpper')
                        : pd.regionName
                          ? t('dashboard.overview.regionPrefixUpper', { region: pd.regionName.toUpperCase() })
                          : t('dashboard.overview.northernRegionUpper'),
                icon: <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B]" />,
                data: getSum(pd.regionProjects || (pd as any).northernProjects),
            },
            {
                title: t('dashboard.overview.keyProjects'),
                icon: <Star className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B]" />,
                data: getSum(pd.keyProjects),
                onClick: () => {
                    navigate({
                        to: '/projects',
                        search: {
                            isKeyProject: 'true',
                            page: 1,
                            ...(activeRegionId && activeRegionId !== 'ALL'
                                ? { regionValues: activeRegionId }
                                : {}),
                            ...(selectedAreaId && selectedAreaId !== 'ALL' ? { areaValues: selectedAreaId } : {}),
                        },
                    })
                },
            },
            {
                title: t('dashboard.overview.otherProjects'),
                icon: <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B]" />,
                data: getSum(pd.otherProjects),
                onClick: () => {
                    navigate({
                        to: '/projects',
                        search: {
                            isKeyProject: 'false',
                            page: 1,
                            ...(activeRegionId && activeRegionId !== 'ALL'
                                ? { regionValues: activeRegionId }
                                : {}),
                            ...(selectedAreaId && selectedAreaId !== 'ALL' ? { areaValues: selectedAreaId } : {}),
                        },
                    })
                },
            },
        ]
    }, [pd, t, navigate, activeRegionId, selectedAreaId])

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-xl font-bold text-foreground sm:text-2xl">{t('dashboard.title')}</h1>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <div className="w-full sm:w-auto">
                        <BsDateRangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            quickOptions={quickOptions}
                            placeholder={t('dashboard.filters.dateRange.placeholder')}
                            className="w-full sm:w-fit"
                            groupClassName="h-9 w-full sm:w-fit"
                        />
                    </div>
                    <Button
                        variant="outline"
                        className="flex items-center gap-2 text-xs sm:text-sm h-9 bg-background"
                        onClick={handleExport}
                    >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="font-normal">{t('dashboard.actions.exportReport')}</span>
                    </Button>
                </div>
            </div>

            {/* Metric Cards */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                {isLoading || !dashboardData ? (
                    <>
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
                        ))}
                    </>
                ) : (
                    metricCards.map((card, index) => (
                        <MetricCard
                            key={index}
                            title={card.title}
                            value={card.value}
                            subtitle={card.subtitle}
                            icon={card.icon}
                            style={{ background: cardBgColor }}
                        />
                    ))
                )}
            </div>

            {/* Project Distribution */}
            <div className="bg-card rounded-lg border p-4 shadow-sm overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                    <h5 className="text-sm font-semibold text-foreground uppercase">{t('dashboard.overview.title')}</h5>
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                        {isSuperAdmin && (
                            <div className="w-full sm:w-56">
                                <BsSelect
                                    selectedKey={selectedRegionId}
                                    onSelectionChange={key => setSelectedRegionId(key as string)}
                                    options={regionOptions}
                                    placeholder={t('dashboard.overview.allRegions')}
                                />
                            </div>
                        )}
                        <div className="w-full sm:w-56">
                            <BsSelect
                                selectedKey={selectedAreaId}
                                onSelectionChange={key => setSelectedAreaId(key as string)}
                                options={areaOptions}
                                placeholder={t('dashboard.overview.allAreas')}
                                isDisabled={isSuperAdmin && (!selectedRegionId || selectedRegionId === 'ALL')}
                            />
                        </div>
                    </div>
                </div>
                {isLoading || !dashboardData ? (
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-64 bg-gray-200 rounded-lg animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                        {distCards.map((card, i) => (
                            <div
                                key={i}
                                onClick={card.onClick}
                                className={cn(
                                    'rounded-xl p-4 border flex flex-col shadow-sm transition-all',
                                    card.onClick &&
                                        'cursor-pointer hover:shadow-md hover:border-primary/50 group/card',
                                )}
                                style={{ background: cardBgColor }}
                            >
                                <div className="font-bold text-sm text-gray-600 mb-4 flex items-center justify-between gap-3">
                                    <div
                                        className={cn(
                                            'text-base line-clamp-2 text-left font-bold text-foreground transition-colors flex items-center gap-1.5',
                                            card.onClick && 'group-hover/card:text-primary',
                                        )}
                                        title={typeof card.title === 'string' ? card.title : undefined}
                                    >
                                        <span className={cn(card.onClick && 'group-hover/card:underline')}>
                                            {card.title}
                                        </span>
                                    </div>
                                    <div
                                        className={cn(
                                            'flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 shrink-0 transition-colors',
                                            card.onClick && 'group-hover/card:bg-primary/30',
                                        )}
                                    >
                                        {card.icon}
                                    </div>
                                </div>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between border-b border-primary/10 pb-2">
                                        <span className="text-gray-500 font-medium">
                                            {t('dashboard.overview.quantity')}
                                        </span>
                                        <span className="font-bold text-base text-gray-900">
                                            {card.data.count}{' '}
                                            <span className="text-xs font-normal text-gray-500">
                                                {t('dashboard.overview.projectUnit')}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="flex justify-between border-b border-primary/10 pb-2">
                                        <span className="text-gray-500 font-medium">
                                            {t('dashboard.overview.investment')}
                                        </span>
                                        <span className="font-bold text-base text-gray-900">
                                            {card.data.investment}{' '}
                                            <span className="text-xs font-normal text-gray-500">
                                                {t('dashboard.overview.billionUnit')}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="space-y-1 border-b border-primary/10 pb-2">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="font-semibold text-gray-600">
                                                {t('dashboard.overview.landScale')}
                                            </span>
                                            <span className="font-bold text-xs text-gray-900">
                                                {card.data.totalArea}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex justify-between pl-1">
                                            <span className="text-gray-500 text-xs">
                                                {t('dashboard.overview.resComServ')}
                                            </span>
                                            <span className="font-semibold text-xs text-gray-900">
                                                {card.data.resCom}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex justify-between pl-1">
                                            <span className="text-gray-500 text-xs">
                                                {t('dashboard.overview.otherLand')}
                                            </span>
                                            <span className="font-semibold text-xs text-gray-900">
                                                {card.data.other}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="space-y-1 pt-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="font-semibold text-gray-600">
                                                {t('dashboard.overview.allocatedLand')}
                                            </span>
                                            <span className="font-bold text-xs text-gray-900">
                                                {card.data.totalAllocatedArea}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex justify-between pl-1">
                                            <span className="text-gray-500 text-xs">
                                                {t('dashboard.overview.resComServ')}
                                            </span>
                                            <span className="font-semibold text-xs text-gray-900">
                                                {card.data.allocResCom}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex justify-between pl-1">
                                            <span className="text-gray-500 text-xs">
                                                {t('dashboard.overview.otherLand')}
                                            </span>
                                            <span className="font-semibold text-xs text-gray-900">
                                                {card.data.allocOther}{' '}
                                                <span className="font-normal text-gray-500">
                                                    {t('dashboard.overview.hectareUnit')}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Main Content Grid */}
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                {/* Project List */}
                <div className="bg-card rounded-lg border p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h5 className="text-sm font-semibold text-foreground">
                            {t('dashboard.sections.projects.title')}
                        </h5>
                        <Button
                            variant="link"
                            className="text-blue-600 text-xs sm:text-sm px-0"
                            onClick={() => navigate({ to: '/projects' })}
                        >
                            <span className="flex items-center gap-1">
                                {t('dashboard.actions.viewAll')}
                                <MoveRight className="w-3 h-3" />
                            </span>
                        </Button>
                    </div>
                    <div className="space-y-4 max-h-114 overflow-y-auto scrollbar-hide">
                        {isLoading || !dashboardData ? (
                            <>
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
                                ))}
                            </>
                        ) : dashboardData?.listProjects?.length ? (
                            dashboardData.listProjects.map(project => (
                                <ProjectItem key={project.id} project={project} />
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <p>{t('dashboard.sections.projects.empty')}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Legal Process List */}
                <div className="bg-card rounded-lg border p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h5 className="text-sm font-semibold text-foreground">
                            {t('dashboard.sections.legalProcesses.title')}
                        </h5>
                        <Button
                            variant="link"
                            className="text-blue-600 text-xs sm:text-sm px-0"
                            onClick={() => navigate({ to: '/legal-documents/tasks' })}
                        >
                            <span className="flex items-center gap-1">
                                {t('dashboard.actions.viewAll')}
                                <MoveRight className="w-3 h-3" />
                            </span>
                        </Button>
                    </div>
                    <div className="space-y-4 max-h-126 overflow-y-auto scrollbar-hide">
                        {isLoading || !dashboardData ? (
                            <>
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="h-20 bg-gray-200 rounded-lg animate-pulse" />
                                ))}
                            </>
                        ) : dashboardData?.listTasks?.length ? (
                            dashboardData.listTasks.map(task => (
                                <LegalProcessItem
                                    key={task.id}
                                    task={task}
                                    getPriorityColorForBadge={getPriorityColorForBadge}
                                    getPriorityLabel={getPriorityLabel}
                                    onTaskClick={() => {
                                        setSelectedTask(task)
                                        setIsTaskDetailOpen(true)
                                    }}
                                />
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <p>{t('dashboard.sections.legalProcesses.empty')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {selectedTask && (
                <TaskDetailDialog
                    open={isTaskDetailOpen}
                    onOpenChange={setIsTaskDetailOpen}
                    taskId={selectedTask.id}
                    onNavigateToTask={task => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setSelectedTask(task as any)
                    }}
                    taskType={selectedTask.taskType}
                />
            )}
        </div>
    )
}

function MetricCard({
    title,
    value,
    subtitle,
    icon,
    style,
}: {
    title: string
    value: string
    subtitle: React.ReactNode
    icon: React.ReactNode
    style?: React.CSSProperties
}) {
    return (
        <div className="rounded-lg p-3 sm:p-4" style={style}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs text-gray-600 mb-1 sm:text-sm mb-2">{title}</p>
                    <p className="font-bold text-gray-900 mb-2 text-xl">{value}</p>
                </div>
                <div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                        <div className="text-white">{icon}</div>
                    </div>
                </div>
            </div>
            {subtitle && <div className="text-xs text-gray-500 leading-tight">{subtitle}</div>}
        </div>
    )
}

function ProjectItem({ project }: { project: ProjectDetail }) {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const deadline = project.endDate ? dayjs(project.endDate).format('DD/MM/YYYY') : t('dashboard.deadline.unknown')
    const progress = project.completionPercentage || 0
    const manager = project.manager

    // Status config matching projects screen
    const statusConfig: Record<string, { label: string; backgroundColor: string; variant: 'default' | 'outline' }> = {
        PREPARING: {
            label: t('projects.list.status.preparation'),
            backgroundColor: 'var(--blue)',
            variant: 'default' as const,
        },
        IN_PROGRESS: {
            label: t('projects.list.status.inProgress'),
            backgroundColor: 'var(--background-yellow)',
            variant: 'default' as const,
        },
        COMPLETED: {
            label: t('projects.list.status.completed'),
            backgroundColor: 'var(--color-green-500)',
            variant: 'default' as const,
        },
        CANCELLED: {
            label: t('projects.list.status.cancelled'),
            backgroundColor: 'var(--color-red-500)',
            variant: 'default' as const,
        },
        ON_HOLD: {
            label: t('projects.list.status.onHold'),
            backgroundColor: 'var(--color-orange-500)',
            variant: 'default' as const,
        },
    }

    const config = statusConfig[project.status] || {
        label: t('projects.list.status.preparation'),
        backgroundColor: 'var(--blue)',
        variant: 'default' as const,
    }

    return (
        <div className="relative border border-border rounded-lg p-4 bg-gray-50 dark:bg-neutral-800/50">
            {/* Header with title and avatars */}
            <div className="flex items-start justify-between mb-2">
                <div className="flex gap-2 flex-col">
                    <p
                        className="font-bold text-foreground cursor-pointer transition-colors mb-0"
                        onClick={() => navigate({ to: `/projects/${project.id}` })}
                    >
                        {project.name}
                    </p>

                    <Badge
                        className="h-5 px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                            backgroundColor: config.backgroundColor,
                            color: 'white',
                        }}
                        title={config.label}
                    >
                        <span className="text-xs mb-0">{config.label}</span>
                    </Badge>
                </div>
                <div className="flex -space-x-1">
                    {manager && (
                        <UserAvatar src={manager.avatar} alt={manager.fullName} fallback={manager.fullName} size="sm" />
                    )}
                </div>
            </div>

            {/* Progress bar and percentage */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 mr-1">
                        <div className="bg-primary h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                    </div>
                    <span className="text-sm text-gray-500 font-medium min-w-8 text-right">{progress}%</span>
                </div>
            </div>

            {/* Deadline */}
            <div className="flex items-center text-xs ">
                <Calendar className="text-red w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span>
                    {t('dashboard.deadline.label')}: {deadline}
                </span>
            </div>
        </div>
    )
}

function LegalProcessItem({
    task,
    getPriorityColorForBadge,
    getPriorityLabel,
    onTaskClick,
}: {
    task: TaskWithFolderPaths
    getPriorityColorForBadge: (priority: string) => string
    getPriorityLabel: (priority: string) => string
    onTaskClick: () => void
}) {
    const folderPathSegments = task.folderPathSegments ?? []
    const folderPathText =
        folderPathSegments.length >= 3
            ? `${folderPathSegments[0]} / ... / ${folderPathSegments[folderPathSegments.length - 1]}`
            : folderPathSegments.join(' / ')

    return (
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border border-border rounded-lg bg-gray-50 dark:bg-neutral-800/50 mb-4">
            <div className="flex-1 min-w-0">
                <h4
                    className="text-sm font-normal text-foreground cursor-pointer transition-colors truncate"
                    onClick={onTaskClick}
                >
                    {task.title}
                </h4>
                {folderPathText && <p className="text-xs text-gray-500 leading-tight line-clamp-2">{folderPathText}</p>}
            </div>
            <div className="flex items-center gap-2">
                <Badge
                    className="h-5 px-2 py-0.5 text-xs font-normal rounded-full"
                    style={{
                        backgroundColor: getPriorityColorForBadge(task.priority),
                        color: 'white',
                    }}
                >
                    {getPriorityLabel(task.priority)}
                </Badge>
                <div className="flex items-center text-xs">
                    <Calendar className="text-red w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span> {dayjs(task.createdAt).format('DD/MM/YYYY')}</span>
                </div>
                <div className="flex -space-x-1">
                    {task.assignedUsers && task.assignedUsers.length > 0 ? (
                        task.assignedUsers
                            .slice(0, 2)
                            .map((user, index) => (
                                <UserAvatar
                                    key={index}
                                    src={user.avatar}
                                    alt={user.fullName}
                                    fallback={user.fullName}
                                    size="sm"
                                    className="border border-white"
                                />
                            ))
                    ) : (
                        <UserAvatar fallback="" size="xs" className="sm:size-7" fallbackClassName="bg-gray-300" />
                    )}
                </div>
                <div className="flex items-center gap-2 border border-border rounded-lg bg-background p-1">
                    <div className="flex items-center justify-center size-4 sm:size-5">
                        <svg className="transform -rotate-90 w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 20 20">
                            <circle
                                cx="10"
                                cy="10"
                                r="8"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                className="text-muted-foreground/30"
                            />
                            <circle
                                cx="10"
                                cy="10"
                                r="8"
                                stroke="currentColor"
                                strokeWidth="2"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 8}`}
                                strokeDashoffset={`${2 * Math.PI * 8 * (1 - (task.progressPercent || 0) / 100)}`}
                                className="text-yellow-500 transition-all duration-300"
                            />
                        </svg>
                    </div>
                    <span className="text-sm text-foreground font-medium leading-none">
                        {task.progressPercent || 0}%
                    </span>
                </div>
            </div>
        </div>
    )
}
